import { Component, computed, inject, input, output, TemplateRef } from '@angular/core';
import {
  ControlContainer,
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
} from '@angular/forms';
import { translateSignal, TranslocoService } from '@jsverse/transloco';
import { VxFormFieldComponent } from '@ui/form-field/form-field.component';
import { VxInputComponent } from '@ui/input/input.component';
import { SharedModule } from 'src/app/core/shared.module';
import { AlertingSettingsModule } from 'src/app/pages/internal/settings/pages/incidents/alerting/alerting.module';
import { VInputFieldComponent } from '../../ui-kit/input-field/input-field.component';
import { VInputV2Component } from '../../ui-kit/input/v2/input.component';
import { VOTPInputComponent } from '../../ui-kit/otp/otp.component';
import { VKratosUiAppearance, VKratosUiInputWidget } from '../types';
import { VxInputType } from '@ui/input/input.variants';

const VX_INPUT_TYPES: readonly string[] = [
  'text',
  'email',
  'number',
  'password',
  'search',
  'tel',
  'url',
];

@Component({
  selector: 'v-kratos-input',
  imports: [
    ReactiveFormsModule,
    SharedModule,
    VInputFieldComponent,
    VInputV2Component,
    AlertingSettingsModule,
    VOTPInputComponent,
    VxFormFieldComponent,
    VxInputComponent,
  ],
  viewProviders: [
    { provide: ControlContainer, useExisting: FormGroupDirective },
  ],
  template: `
    @let i = widget();
    @let attrs = i.attributes;

    <form class="flex" [formGroup]="formGroup()" (ngSubmit)="onSubmit($event)">
      <div class="flex-1">
        @if (appearance() === 'vx') {
          @if (attrs.type === 'hidden') {
            <input type="hidden" [formControlName]="i.id" />
          } @else if (attrs.name === 'totp_code') {
            <!-- The OTP control has no VX_FORM_CONTROL token, so the field
                 wrapper could not surface its server errors — laid out and
                 announced manually instead. -->
            <div class="flex flex-col gap-1.5">
              <span class="text-sm tracking-tight text-zinc-200">
                {{ label() }}
              </span>
              <v-otp-input
                [size]="'md'"
                [testId]="i.id"
                [fControlName]="i.id"
                (submitCode)="onSubmit($event)"></v-otp-input>
              @for (message of serverErrorMessages(); track $index) {
                <p class="text-xs tracking-tight text-danger-400" role="alert">
                  {{ message }}
                </p>
              }
            </div>
          } @else {
            <vx-form-field [label]="label()" [errors]="serverErrors()">
              <vx-input
                [type]="vxType()"
                [autocomplete]="attrs.autocomplete"
                [placeholder]="placeholder()"
                [formControlName]="i.id" />
            </vx-form-field>
          }
        } @else {
          <v-input-field
            [controlId]="i.id"
            [label]="label()"
            [fControlName]="i.id"
            [headerComponent]="headerComponent()"
            additionalLabelClasses="text-surface-1-v2-contrast-subtle!">
            @if (attrs.name === 'totp_code') {
              <v-otp-input
                [size]="'md'"
                [testId]="i.id"
                [fControlName]="i.id"
                (submitCode)="onSubmit($event)"></v-otp-input>
            } @else {
              <v-input-v2
                [type]="attrs.type"
                [autocomplete]="attrs.autocomplete"
                [testId]="i.id"
                [placeholder]="placeholder()"
                [fControlName]="i.id"></v-input-v2>
            }
          </v-input-field>
        }
      </div>
    </form>
  `,
})
export class VKratosInputComponent {
  private translocoService = inject(TranslocoService);

  widget = input.required<VKratosUiInputWidget>();
  formGroup = input.required<FormGroup>();
  headerComponent = input<TemplateRef<any>>();
  appearance = input<VKratosUiAppearance>('default');

  submitForm = output<{
    event: Event;
    widget: VKratosUiInputWidget;
  }>();

  onSubmit(event: Event) {
    this.submitForm.emit({
      event: event,
      widget: this.widget(),
    });
  }

  placeholderTranslationKey = computed(() => {
    const attributeName = this.widget().attributes.name;
    return `kratosMessages.inputPlaceholders.${attributeName}`;
  });

  labelTranslationKey = computed(() => {
    const attributeName = this.widget().attributes.name;
    return `kratosMessages.inputLabels.${attributeName}`;
  });

  placeholder = translateSignal(this.placeholderTranslationKey);
  label = translateSignal(this.labelTranslationKey);

  /** The widget's input type, narrowed to what vx-input supports. */
  vxType = computed<VxInputType>(() => {
    const type = this.widget().attributes.type as string;
    return (VX_INPUT_TYPES.includes(type) ? type : 'text') as VxInputType;
  });

  /**
   * Kratos server messages for `vx-form-field`. The form host sets them on
   * the control as an object spread of the message array, so the failing
   * "validator" keys are the array indices — this map mirrors that keying.
   */
  serverErrors = computed<Record<string, string>>(() => {
    const messages = this.widget().messages ?? [];
    return Object.fromEntries(
      messages.map((m, index) => [
        `${index}`,
        this.translocoService.translate(`kratosMessages.${m.id}`, m.context),
      ])
    );
  });

  serverErrorMessages = computed<string[]>(() =>
    Object.values(this.serverErrors())
  );
}
